import { FileSystemItemType, IFileSystemItem, WikiMode } from '../../wiki-interfaces';
import { ArrowDownIcon, ArrowRightIcon } from '../../../../common/ui/icons';
import { closeWikiFolder, openWikiFolder } from '../../domain/wiki-storage';
import { encodePath } from '../../domain/wiki-provider';
import { useNavigate } from 'react-router-dom';
import WikiMenuDragButton from './wiki-menu-drag-btn';
import WikiMenuDragTarget from './wiki-menu-drag-target';
import { closeMobileMenu } from '../../../../menu/ui/mobile-menu-provider';
import { isExternalFile } from '../../../../common/domain/file-provider';
import { useWikiStore } from '../../data/wiki-store';

interface IWikiMenuItemProps {
    item: IFileSystemItem;
}

const getColor = (isSelected: boolean, isFolder: boolean) => {
    if(isSelected) {
        return 'text-amber-300';
    }

    if(isFolder) {
        return 'text-violet-300';
    }

    return 'text-slate-100';
};

const WikiMenuItem = ({ item } : IWikiMenuItemProps) => {

    const navigate = useNavigate();

    const openedFolders = useWikiStore(store => store.openedFolders);
    const setOpenedFolders = useWikiStore(store => store.setOpenedFolders);

    const wikiSelectedPath = useWikiStore(store => store.wikiSelectedPath);
    const setWikiSelectedPath = useWikiStore(store => store.setWikiSelectedPath);
    const setWikiMode = useWikiStore(store => store.setWikiMode);

    const isSelected = wikiSelectedPath === item.path;
    const isFolder = item.type === FileSystemItemType.FOLDER;
    const isExternal = !isFolder && isExternalFile(item.path);

    const toggleFolder = async () => {

        const isOpened = openedFolders.has(item.path);
        const copy = new Set(openedFolders);

        if(isOpened) {
            closeWikiFolder(item.path);
            copy.delete(item.path);
        }
        else{
            openWikiFolder(item.path);
            copy.add(item.path);
        }

        setOpenedFolders(copy);
    };

    const openItem = async () => {
        switch (item.type) {

            case FileSystemItemType.FILE: {
                setWikiSelectedPath(item.path);
                setWikiMode(WikiMode.DEFAULT);
                navigate(`/wiki${ encodePath(item.path) }`);
                break;
            }

            case FileSystemItemType.FOLDER: {
                setWikiSelectedPath(item.path);
                setWikiMode(WikiMode.VIEW_FOLDER);
                navigate(`/wiki${ encodePath(item.path) }`);
                break;
            }
        }

        closeMobileMenu();
    };

    return (
        <div className="wiki__menu-item flex flex-col">

            <div className={ `flex items-start ${ getColor(isSelected, isFolder) }` }>
                {
                    isFolder &&
                    <button type="button" onClick={ toggleFolder }>
                        { openedFolders.has(item.path) && <ArrowDownIcon color={ '#94A3B8' } /> }
                        { !openedFolders.has(item.path) && <ArrowRightIcon color={ '#94A3B8' } /> }
                    </button>
                }

                {/* Only to keep the same space */}
                {
                    !isFolder &&
                    <button type="button" className="invisible">
                        <ArrowRightIcon color={ '#88a3d0' } />
                    </button>
                }

                {
                    isExternal &&
                    <a
                        href={ `/api/wiki/get-file?file_path=${ item.path }` }
                        className="wiki__menu-item__title text-left border-b border-slate-700 pb-[3px] mb-[3px] flex-1"
                        target="_blank"
                        rel="noreferrer">{ item.name }</a>
                }

                {
                    !isExternal &&
                    <button
                        className="wiki__menu-item__title text-left border-b border-slate-700 pb-[3px] mb-[3px] flex-1"
                        onClick={ openItem }
                        type="button">{ item.name }</button>
                }

                <WikiMenuDragButton item={ item } visible={ isSelected } />
            </div>

            {
                isFolder &&
                <WikiMenuDragTarget itemPath={ item.path } />
            }

            {
                openedFolders.has(item.path) && item.nested && item.nested.length > 0 &&
                <div className="ml-4">
                    {
                        item.nested.map(
                            nestedItem =>
                                <WikiMenuItem
                                    key={ nestedItem.path }
                                    item={ nestedItem } />
                        )
                    }
                </div>
            }
        </div>
    )
};

export default WikiMenuItem;